import { useEffect, useState, useRef } from 'react';
import { useQuery } from "@tanstack/react-query";
import useUserToken from '../../context/userTokenContext';
import { getGateways, getGatewaysCounters } from '../../api/apiCalls';
import SmartCornerDataTable from '../dataTables/smartCornerDataTable';
import Stats from '../stats';
import useNavbarSearchStore from '../../context/useNavBarSearchStore';
import GatewaysMap from '../mapComponents/gatewaysMap';
import useLocationsStore from '../../context/locationsStore';

function GatewaysPage() {
    const [success, setSuccess] = useState(false);
    const [countersSuccess, setCountersSuccess] = useState(false);
    const [gatewayCounters, setGatewayCounters] = useState([]);
    const [gatewayList, setGatewayList] = useState([]);

    const { locations } = useLocationsStore();
    const { setSearchList } = useNavbarSearchStore();

    const [targetMarker, setTargetMarker] = useState(null);

    const mapRef = useRef(null);

    const handleZoomToLocation = (coordinates, zoomLevel) => {
        if (mapRef.current) {
            mapRef.current.zoomToCoordinates(coordinates, zoomLevel);
        }
    };

    const statusObject = {
        0: 'fail',
        1: 'active',
        2: 'init',
        3: 'error'
    };

    const { data: gatewaysData, isLoading: gatewaysIsLoading, isError: gatewaysIsError, error: gatewaysError } = useQuery({
        queryKey: ['getGateways'],
        queryFn: getGateways
    });

    const { data: gatewayCountersData, isLoading: gatewayCountersIsLoading, isError: gatewayCountersIsError, error: gatewayCountersError } = useQuery({
        queryKey: ['getGatewaysCounters'],
        queryFn: getGatewaysCounters
    });

    useEffect(() => {
        let listArr = [];
        let searchListArr = [];

        if (gatewaysData && gatewaysData.data) {
            Object.entries(gatewaysData.data.domains).forEach(([domainName, domainData]) => {
                domainData.list.forEach((gateway) => {
                    listArr.push({
                        name: gateway.name,
                        eui: gateway.lrruuid,
                        id: gateway.lrrid,
                        status: statusObject[gateway.status],
                        locations: locations[domainName]?.name || "Unknown Location",
                        server: gateway.server,
                        latitude: gateway.latitude,
                        longitude: gateway.longitude,
                        altitude: gateway.altitude,
                    });

                    searchListArr.push(
                        {
                            name: gateway.name,
                            id: gateway.lrrid,
                            server: gateway.server
                        }
                    );
                });
            });
            setGatewayList(listArr);
            setSearchList(searchListArr);
            setSuccess(true);
        }
    }, [gatewaysData, locations]); // Add locations to dependencies

    useEffect(() => {
        if (gatewayCountersData && gatewayCountersData.data) {
            let totalCounters = { total: 0, active: 0, fail: 0, init: 0, error: 0 };
            Object.entries(gatewayCountersData.data.groups).forEach(([domainName, domainData]) => {
                if (domainName !== 'global') {
                    totalCounters.total += domainData.total;
                    totalCounters.active += domainData.active;
                    totalCounters.fail += domainData.fail;
                    totalCounters.init += domainData.init;
                    totalCounters.error += domainData.error;
                }
            });
            setGatewayCounters([totalCounters]);
            setCountersSuccess(true);
        }
    }, [gatewayCountersData]);

    useEffect(() => {
        if (targetMarker) {
            handleZoomToLocation([targetMarker.latitude, targetMarker.longitude], 16);
        }
    }, [targetMarker]);

    return (
        <div>
            {gatewayCountersIsError && <p>Error loading counters: {gatewayCountersError.message}</p>}
            {gatewaysIsError && <p>Error loading gateways: {gatewaysError.message}</p>}

            <div className='mt-10'>
                {countersSuccess ?
                    <div className='flex flex-row justify-start items-start'>
                        <p className='flex justify-start text-2xl font-bold'>Total Client's Gateways</p>
                        <span className="badge badge-outline ml-4 text-[var(--textPrimary)] font-bold text-nowrap">{new Date(gatewayCountersData.data.lastUpdate).toLocaleString('en-GB')}</span>
                    </div>
                    :
                    <div>
                        <p className='flex justify-start text-2xl font-bold'>Total Client's Gateways</p>
                        <span className="animate-pulse bg-[var(--skeleton-bg)] rounded-full h-4 w-36"></span>
                    </div>
                }
            </div>

            {countersSuccess ?
                <Stats counters={gatewayCounters} type="gateways" />
                :
                <Stats skeleton={true} />
            }

            {success && !gatewaysIsError &&
                <div className='w-full bg-[var(--primary)] rounded-2xl mt-6'>
                    <GatewaysMap center={[38.27, 23.81]} zoom={6} ref={mapRef} markers={gatewayList} />
                </div>
            }


            {success && !gatewaysIsError &&
                <div className='w-full bg-[var(--primary)] rounded-2xl mt-6 mb-20'>
                    <SmartCornerDataTable data={gatewayList} setTargetMarker={setTargetMarker} />
                </div>
            }
        </div>
    );
}

export default GatewaysPage;
